import React, { useContext, useEffect, useMemo, useState } from 'react';
import V5GadgetForm from '../V5GadgetForm';
import Dropdown from '../Inputs/Dropdown';
import toSentenceCase from '../../services/toSentenceCase';
import SingleMetricDateMatrixForm from './SingleMetricDateMatrixForm';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import EntityInformationTableForm from '../EntityInformationTableForm';
import GaugeForm from '../GaugeForm';
import isSingleMetricDateMatrix from '../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isEntityInformationTable from '../../types/visTypeCheckers/isEntityInformationTable';
import isGauge from '../../types/visTypeCheckers/isGauge';
import RemindersGadgetForm from '../RemindersGadgetForm';
import isRemindersGadget from '../../types/visTypeCheckers/isRemindersGadget';
import getGadgetType from '../../getGadgetType';
import RankingMatrixForm from '../RankingMatrixForm';
import isRankingMatrix from '../../types/visTypeCheckers/isRankingMatrix';
import isPaceMatrix from '../../types/visTypeCheckers/isPaceMatrix';
import PaceMatrixFormContainer from './PaceMatrixForm';
import GroupVsRemainderFormContainer from './GroupVsRemainderForm';
import isGroupVsRemainder from '../../types/visTypeCheckers/isGroupVsRemainder';
import { useParams } from 'react-router-dom';
import GADGETS from './GADGETS';
import styled from 'styled-components';
import { TOP_BAR_HEIGHT } from '../../constants';
import { Scrollable } from '../UserPicker/UserPicker';

const GadgetFormHeader = styled.div`
  height: ${TOP_BAR_HEIGHT}px;
  border-bottom: 1px solid var(--color-border-primary);
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 12px;
  padding: 0px 24px;
  padding-bottom: 4px;
`;

export const GadgetFormContainer = ({
  selected,
  onChange,
  isEditing = false,
  isReport = false,
}: {
  selected?: VisualisationDefinition;
  onChange?: React.Dispatch<
    React.SetStateAction<VisualisationDefinition | undefined>
  >;
  isEditing?: boolean;
  isReport?: boolean;
}) => {
  const { gadgetId } = useParams<{ gadgetId?: string }>();

  const { trackEvent } = useContext(AnalyticsContext);
  const [selectedGadgetType, setSelectedGadgetType] = useState<
    GadgetType | VisualisationDefinitions.GadgetType | undefined
  >(() => {
    if (selected) {
      return getGadgetType(selected);
    }
    return undefined;
  });
  const [gadgetTypeLabel, setGadgetTypeLabel] = useState<string>(() => {
    if (selected) {
      const type = getGadgetType(selected);
      const gadget = GADGETS.find((g) => g.name === type);
      return gadget?.displayName || '';
    }
    return '';
  });

  useEffect(() => {
    if (!gadgetId) {
      setSelectedGadgetType(undefined);
    } else if (selected) {
      trackEvent('Gadget Builder - Edit Opened', {
        gadgetName: selected.name,
        gadgetId: selected.id,
      });
    } else {
      trackEvent('Gadget Builder - New Opened');
    }
  }, [gadgetId, selected, trackEvent]);

  useEffect(() => {
    if (selected) {
      const type = getGadgetType(selected);
      setSelectedGadgetType(type);
      const gadget = GADGETS.find((g) => g.name === type);
      if (gadget) {
        setGadgetTypeLabel(gadget.displayName);
      }
    }
  }, [selected]);

  const gadgetOptions = useMemo(() => {
    const filteredGadgets = isReport
      ? GADGETS.filter((t) => t.name !== 'Reminders')
      : GADGETS;

    return filteredGadgets.map((t) => ({
      label: toSentenceCase(t.displayName),
      onSelected: () => {
        setSelectedGadgetType(t.name);
        setGadgetTypeLabel(t.displayName);
        trackEvent('Gadget Builder - Type selected', {
          gadgetType: t.name,
        });
      },
    }));
  }, [isReport, trackEvent]);

  let gadgetForm = null;
  if (!selectedGadgetType) {
    gadgetForm = null;
  } else if (selectedGadgetType === 'SingleMetricDateMatrix') {
    gadgetForm = (
      <SingleMetricDateMatrixForm
        gadget={isSingleMetricDateMatrix(selected) ? selected : undefined}
        onChange={onChange}
      />
    );
  } else if (selectedGadgetType === 'Gauge') {
    gadgetForm = (
      <GaugeForm
        gauge={isGauge(selected) ? selected : undefined}
        onChange={onChange}
      />
    );
  } else if (selectedGadgetType === 'Reminders') {
    gadgetForm = (
      <RemindersGadgetForm
        gadget={isRemindersGadget(selected) ? selected : undefined}
        onChange={onChange}
      />
    );
  } else if (selectedGadgetType === 'RankingMatrix') {
    gadgetForm = (
      <RankingMatrixForm
        gadget={isRankingMatrix(selected) ? selected : undefined}
        onChange={onChange}
      />
    );
  } else if (selectedGadgetType === 'PaceMatrix') {
    gadgetForm = (
      <PaceMatrixFormContainer
        onDraftChanged={onChange}
        editingPaceGadget={isPaceMatrix(selected) ? selected : undefined}
      />
    );
  } else if (selectedGadgetType === 'GroupVsRemainder') {
    gadgetForm = (
      <GroupVsRemainderFormContainer
        onChange={onChange}
        gadget={isGroupVsRemainder(selected) ? selected : undefined}
      />
    );
  } else if (selectedGadgetType === 'EntityInformationTable') {
    gadgetForm = (
      <EntityInformationTableForm
        gadget={isEntityInformationTable(selected) ? selected : undefined}
        onChange={onChange}
        isReport={isReport}
      />
    );
  } else {
    gadgetForm = (
      <V5GadgetForm
        gadgetType={selectedGadgetType}
        onChange={onChange}
        dashboardGadgetChart={selected}
      />
    );
  }

  return (
    <div
      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      data-testid="gadget-form-container"
      key={gadgetId}
    >
      <GadgetFormHeader>
        <Dropdown
          isSearchEnabled
          testId="gadget-picker"
          placeholder={'Gadget Type'}
          selectedLabel={gadgetTypeLabel}
          options={gadgetOptions}
        />
      </GadgetFormHeader>
      <Scrollable style={{ height: '100%', flex: 1, maxHeight: '100%' }}>
        {gadgetForm}
      </Scrollable>
    </div>
  );
};

export default GadgetFormContainer;
